/**
 * Card visual customization. There are two independent dimensions:
 *
 *   - **Face style** (`CardStyle`) — the colors used on the front of the
 *     card: suit ink, background, face-card box, joker tint.
 *   - **Card back** (`CardBack`) — the design shown when the card is
 *     face-down (or for the deck pile).
 *
 * A player can mix and match: e.g. mono faces with a crimson back. Each
 * preference is stored on the `players` row server-side and read via
 * `/api/me`. The whole app is wrapped in both providers in `App.tsx`.
 *
 * Adding more options is a one-entry change to `CARD_STYLES` /
 * `CARD_BACKS` — `PlayingCard` and `SettingsSheet` pick them up
 * automatically.
 */

import { createContext, useContext, type ReactNode } from "react";
import type { CardBackId, CardThemeId } from "../../../shared/protocol.js";

// ---------------------------------------------------------------------------
// Face style
// ---------------------------------------------------------------------------

export interface CardStyle {
  id: CardThemeId;
  label: string;
  description: string;
  /** Face background. */
  face: string;
  /** Border / hairline color of the face. */
  border: string;
  /** Stroke width multiplier — higher = bolder card border. */
  borderStrokeMul: number;
  /** Corner rank label font weight. */
  cornerWeight: number;
  /** Corner rank font size in viewBox units. */
  cornerRankSize: number;
  /** Corner suit-glyph font size. */
  cornerSuitSize: number;
  /** Pip glyph size in viewBox units. */
  pipSize: number;
  /** Suit colors keyed by suit letter. "J" is the joker color. */
  suit: {
    C: string;
    D: string;
    H: string;
    S: string;
    J: string;
  };
  /** Default ink color when not overridden by suit color. */
  ink: string;
  /** Face-card center-box fill. */
  faceBox: string;
}

export const CARD_STYLES: Record<CardThemeId, CardStyle> = {
  bold: {
    id: "bold",
    label: "Bold",
    description: "Crisp white face, deeply saturated suits, heavy corner ink.",
    face: "#ffffff",
    border: "#000000",
    // Bolder than the previous "bold" — heavier border, fatter glyphs,
    // chunkier pip art. This is the default for every new player.
    borderStrokeMul: 1.6,
    cornerWeight: 800,
    cornerRankSize: 17,
    cornerSuitSize: 14,
    pipSize: 22,
    suit: {
      C: "#000000",
      D: "#e21d1d",
      H: "#e21d1d",
      S: "#000000",
      J: "#6d28d9",
    },
    ink: "#000000",
    faceBox: "#ffffff",
  },
  mono: {
    id: "mono",
    label: "Mono",
    description: "Minimalist grayscale. One ink color, restrained suit glyphs.",
    face: "#e9e9e7",
    border: "#1a1a1a",
    borderStrokeMul: 1,
    cornerWeight: 700,
    cornerRankSize: 14,
    cornerSuitSize: 12,
    pipSize: 18,
    suit: {
      C: "#1a1a1a",
      D: "#1a1a1a",
      H: "#1a1a1a",
      S: "#1a1a1a",
      J: "#1a1a1a",
    },
    ink: "#1a1a1a",
    faceBox: "#f5f5f3",
  },
};

const CardStyleContext = createContext<CardStyle>(CARD_STYLES.bold);

export function CardStyleProvider({
  styleId,
  children,
}: {
  styleId: CardThemeId;
  children: ReactNode;
}) {
  const style = CARD_STYLES[styleId] ?? CARD_STYLES.bold;
  return <CardStyleContext.Provider value={style}>{children}</CardStyleContext.Provider>;
}

export function useCardStyle(): CardStyle {
  return useContext(CardStyleContext);
}

// ---------------------------------------------------------------------------
// Card back
// ---------------------------------------------------------------------------

export interface CardBack {
  id: CardBackId;
  label: string;
  /** Outer field color. */
  primary: string;
  /** Inner panel color (inset from the outer). */
  secondary: string;
  /** Stroke color used for the diagonal hatch pattern. */
  pattern: string;
  /** Color of the round medallion fill. */
  medallion: string;
  /** Color of the "T" inside the medallion. */
  medallionInk: string;
}

export const CARD_BACKS: Record<CardBackId, CardBack> = {
  blue: {
    id: "blue",
    label: "Blue",
    primary: "#1c4a6d",
    secondary: "#163955",
    pattern: "#3a6b94",
    medallion: "#0a2336",
    medallionInk: "#d4af37",
  },
  forest: {
    id: "forest",
    label: "Forest",
    primary: "#1c5836",
    secondary: "#0f3a22",
    pattern: "#3aa172",
    medallion: "#0a2316",
    medallionInk: "#f5d061",
  },
  crimson: {
    id: "crimson",
    label: "Crimson",
    primary: "#7a1f1f",
    secondary: "#4f1212",
    pattern: "#b94646",
    medallion: "#2a0808",
    medallionInk: "#f5d061",
  },
  slate: {
    id: "slate",
    label: "Slate",
    primary: "#2e3338",
    secondary: "#1c2024",
    pattern: "#5a6168",
    medallion: "#0a0d10",
    medallionInk: "#cfd5dc",
  },
  gold: {
    id: "gold",
    label: "Gold",
    primary: "#7a5712",
    secondary: "#4a3508",
    pattern: "#cf9d3a",
    medallion: "#1f1505",
    medallionInk: "#fff2c2",
  },
};

const CardBackContext = createContext<CardBack>(CARD_BACKS.blue);

export function CardBackProvider({
  backId,
  children,
}: {
  backId: CardBackId;
  children: ReactNode;
}) {
  const back = CARD_BACKS[backId] ?? CARD_BACKS.blue;
  return <CardBackContext.Provider value={back}>{children}</CardBackContext.Provider>;
}

export function useCardBack(): CardBack {
  return useContext(CardBackContext);
}
